'use client';

import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';

interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  title: string;
  message?: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  isDestructive?: boolean;
  isWarning?: boolean;
  confirmDisabled?: boolean;
  showIcon?: boolean;
  customContent?: React.ReactNode;
  customActions?: React.ReactNode;
  size?: 'sm' | 'lg';
}

export default function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  isDestructive = true,
  isWarning = false,
  confirmDisabled = false,
  showIcon = true,
  customContent,
  customActions,
  size = 'sm',
}: ConfirmModalProps) {
  const { darkMode } = useDarkMode();

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-[110]">
      <DialogBackdrop
        transition
        className={`fixed inset-0 backdrop-blur-[2px] transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in ${
          darkMode ? 'bg-gray-900/75' : 'bg-gray-500/75'
        }`}
      />

      <div className="fixed inset-0 z-[110] w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className={`relative transform overflow-hidden rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full ${
              size === 'lg' ? 'sm:max-w-2xl' : 'sm:max-w-lg'
            } sm:p-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95 ${
              darkMode ? 'border border-white/10 bg-gray-800' : 'bg-white'
            }`}
          >
            <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
              <button
                type="button"
                onClick={onClose}
                className={`rounded-md focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden ${
                  darkMode
                    ? 'bg-gray-800 text-gray-400 hover:text-gray-300'
                    : 'bg-white text-gray-400 hover:text-gray-500'
                }`}
              >
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>
            {customContent ? (
              <div>
                <DialogTitle
                  as="h3"
                  className={`mb-4 text-base font-semibold ${
                    darkMode ? 'text-gray-100' : 'text-gray-900'
                  }`}
                >
                  {title}
                </DialogTitle>
                {customContent}
              </div>
            ) : (
              <div className="sm:flex sm:items-start">
                {showIcon && (
                  <div
                    className={`mx-auto flex size-12 shrink-0 items-center justify-center rounded-full sm:mx-0 sm:size-10 ${
                      isDestructive
                        ? darkMode
                          ? 'bg-red-900/20'
                          : 'bg-red-100'
                        : darkMode
                          ? 'bg-yellow-900/20'
                          : 'bg-yellow-100'
                    }`}
                  >
                    <ExclamationTriangleIcon
                      aria-hidden="true"
                      className={`size-6 ${
                        isDestructive
                          ? darkMode
                            ? 'text-red-400'
                            : 'text-red-600'
                          : darkMode
                            ? 'text-yellow-400'
                            : 'text-yellow-600'
                      }`}
                    />
                  </div>
                )}
                <div
                  className={`mt-3 text-center ${showIcon ? 'sm:mt-0 sm:ml-4' : ''} sm:text-left`}
                >
                  <DialogTitle
                    as="h3"
                    className={`text-base font-semibold ${
                      darkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}
                  >
                    {title}
                  </DialogTitle>
                  {message && (
                    <div className="mt-2">
                      {typeof message === 'string' ? (
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {message}
                        </p>
                      ) : (
                        <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {message}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            {customActions ? (
              customActions
            ) : (
              <div
                className={`mt-5 sm:mt-4 ${cancelText ? 'sm:flex sm:flex-row-reverse' : 'flex justify-center'}`}
              >
                {confirmText && (
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={confirmDisabled}
                    className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-xs ${cancelText ? 'sm:ml-3 sm:w-auto' : 'sm:w-auto'} ${
                      isDestructive
                        ? confirmDisabled
                          ? 'cursor-not-allowed bg-red-400'
                          : 'bg-red-600 hover:bg-red-500'
                        : isWarning
                          ? confirmDisabled
                            ? 'cursor-not-allowed bg-yellow-400'
                            : 'bg-yellow-600 hover:bg-yellow-500'
                          : confirmDisabled
                            ? 'cursor-not-allowed bg-indigo-400'
                            : 'bg-indigo-600 hover:bg-indigo-500'
                    }`}
                  >
                    {confirmText}
                  </button>
                )}
                {cancelText && (
                  <button
                    type="button"
                    onClick={onClose}
                    className={`mt-3 inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-xs ring-1 ring-inset sm:mt-0 sm:w-auto ${
                      darkMode
                        ? 'bg-white/5 text-gray-300 ring-white/10 hover:bg-white/10'
                        : 'bg-white text-gray-900 ring-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {cancelText}
                  </button>
                )}
              </div>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
